import { LoaderFunctionArgs, ActionFunctionArgs } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { QueryClient } from '@tanstack/react-query';
import { getConfig } from 'src/config/config';

export enum DownloadReportAction {
  REQUEST_OTP = 'requestOtp',
  DOWNLOAD_SHARED_REPORT = 'downloadSharedReport',
}

export type OtpRequestResult = { ok: boolean; rateLimited: boolean };

export type UnlockDownloadError =
  | 'incorrect'
  | 'expired'
  | 'rate_limit'
  | 'unknown';

export type DownloadReportActionResult =
  | { action: DownloadReportAction.REQUEST_OTP; otpResult: OtpRequestResult }
  | {
      action: DownloadReportAction.DOWNLOAD_SHARED_REPORT;
      url?: string;
      error?: UnlockDownloadError;
    };

export async function requestOtp(
  token: string,
): Promise<{ ok: boolean; rateLimited: boolean }> {
  const baseUrl = getConfig().apiBaseUrl.replace(/\/$/, '');
  const res = await fetch(`${baseUrl}/shared_reports/${token}/otp`, {
    method: 'POST',
  });
  if (res.status === 200) return { ok: true, rateLimited: false };
  if (res.status === 429) return { ok: false, rateLimited: true };
  return { ok: false, rateLimited: false };
}

async function requestDownloadUrl(
  token: string,
  otp: string,
): Promise<
  { ok: true; url: string } | { ok: false; error: UnlockDownloadError }
> {
  const baseUrl = getConfig().apiBaseUrl.replace(/\/$/, '');
  const res = await fetch(`${baseUrl}/shared_reports/${token}/access`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json; charset=UTF-8' },
    body: JSON.stringify({ otp }),
  });
  if (res.status === 200) {
    const data = (await res.json()) as { url: string };
    return { ok: true, url: data.url };
  }
  if (res.status === 403 || res.status === 400 || res.status === 404)
    return { ok: false, error: 'incorrect' };
  if (res.status === 410) return { ok: false, error: 'expired' };
  if (res.status === 429) return { ok: false, error: 'rate_limit' };
  return { ok: false, error: 'unknown' };
}

export type DownloadReportLoaderData = {
  token: string | null;
  otpResult: OtpRequestResult | null;
};

export const downloadReportLoader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs): Promise<DownloadReportLoaderData> => {
    const token = params.token ?? null;
    if (!token) {
      return { token: null, otpResult: null };
    }
    const otpResult = await queryClient.ensureQueryData<OtpRequestResult>({
      queryKey: ['downloadReportOtp', token],
      queryFn: () => requestOtp(token),
    });
    return { token, otpResult };
  };

export const downloadReportAction =
  (queryClient: QueryClient) =>
  async ({
    request,
    params,
  }: ActionFunctionArgs): Promise<DownloadReportActionResult | undefined> => {
    const formData = await request.formData();
    const actionType = formData.get(
      'actionType',
    ) as DownloadReportAction | null;
    const token =
      (formData.get('token') as string | null) ?? params.token ?? '';

    try {
      if (actionType === DownloadReportAction.REQUEST_OTP) {
        queryClient.invalidateQueries({
          queryKey: ['downloadReportOtp', token],
        });
        return;
      }

      if (actionType === DownloadReportAction.DOWNLOAD_SHARED_REPORT) {
        const otp = (formData.get('otp') as string)?.trim() ?? '';
        const result = await requestDownloadUrl(token, otp);
        if (result.ok) {
          return {
            action: DownloadReportAction.DOWNLOAD_SHARED_REPORT,
            url: result.url,
          };
        }
        return {
          action: DownloadReportAction.DOWNLOAD_SHARED_REPORT,
          error: result.error,
        };
      }

      return {
        action: DownloadReportAction.DOWNLOAD_SHARED_REPORT,
        error: 'unknown',
      };
    } catch (error) {
      Sentry.captureException(error, {
        tags: { action: actionType },
        extra: { message: 'Download shared report error' },
      });
    }
  };
